import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import RevenueChart from '../components/RevenueChart';
import PlanDistribution from '../components/PlanDistribution';
import CrossAppKPIs from '../components/CrossAppKPIs';
import PackagesManager from '../components/PackagesManager';
import ModulesSSO from '../components/ModulesSSO';
import {
  Shield,
  Building2,
  CreditCard,
  Receipt,
  Settings,
  ScrollText,
  Search,
  ChevronDown,
  Loader2,
  Users,
  DollarSign,
  TrendingUp,
  Play,
  Pause,
  Gift,
  Download,
  Save,
  Clock,
  RefreshCw,
  Package,
  AppWindow,
  BarChart3,
  AlertCircle,
  RotateCcw,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'structures', label: 'Structures', icon: Building2 },
  { key: 'subscriptions', label: 'Abonnements', icon: CreditCard },
  { key: 'packages', label: 'Packages', icon: Package },
  { key: 'modules', label: 'Modules', icon: AppWindow },
  { key: 'invoices', label: 'Factures', icon: Receipt },
  { key: 'settings', label: 'Parametres', icon: Settings },
  { key: 'audit', label: 'Audit Logs', icon: ScrollText },
];

const STATUS_BADGE = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-slate-100 text-slate-500',
  suspended: 'bg-red-100 text-red-700',
  trial: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-red-100 text-red-700',
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  overdue: 'bg-red-100 text-red-700',
};

const STAT_CARD_CONFIG = [
  {
    key: 'mrr',
    label: 'MRR',
    icon: DollarSign,
    iconBg: 'bg-green-50',
    iconColor: 'text-green-600',
    format: 'eur',
    extract: (s) => s.revenue?.mrr ?? s.mrr ?? 0,
  },
  {
    key: 'arr',
    label: 'ARR',
    icon: TrendingUp,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    format: 'eur',
    extract: (s) => s.revenue?.arr ?? s.arr ?? 0,
  },
  {
    key: 'structures',
    label: 'Structures',
    icon: Building2,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
    format: 'number',
    extract: (s) => s.structures?.total ?? s.total_structures ?? 0,
  },
  {
    key: 'users',
    label: 'Utilisateurs',
    icon: Users,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    format: 'number',
    extract: (s) => s.users?.total ?? s.total_users ?? 0,
  },
  {
    key: 'subscriptions',
    label: 'Abonnements',
    icon: CreditCard,
    iconBg: 'bg-indigo-50',
    iconColor: 'text-indigo-600',
    format: 'number',
    extract: (s) => s.subscriptions?.total ?? s.total_subscriptions ?? 0,
  },
  {
    key: 'apps',
    label: 'Applications',
    icon: AppWindow,
    iconBg: 'bg-teal-50',
    iconColor: 'text-teal-600',
    format: 'number',
    extract: (s) => s.apps_count ?? (Object.keys(s.subscriptions?.by_app || {}).length || 0),
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatEUR = (value) => {
  if (value == null) return '--';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (val) => {
  if (val == null) return '--';
  return new Intl.NumberFormat('fr-FR').format(val);
};

const formatStatValue = (format, value) => {
  if (value == null) return '--';
  if (format === 'eur') return formatEUR(value);
  return formatNumber(value);
};

// ---------------------------------------------------------------------------
// Skeleton loaders
// ---------------------------------------------------------------------------

const StatCardSkeleton = () => (
  <div className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-slate-100" />
      <div className="flex-1">
        <div className="h-3 bg-slate-100 rounded w-16 mb-2" />
        <div className="h-7 bg-slate-100 rounded w-20" />
      </div>
    </div>
  </div>
);

const ChartSkeleton = () => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 animate-pulse">
    <div className="h-4 bg-slate-100 rounded w-32 mb-6" />
    <div className="h-64 bg-slate-50 rounded-lg" />
  </div>
);

const TableSkeleton = () => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-pulse">
    <div className="h-12 bg-slate-50 border-b border-slate-200" />
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex gap-4 px-4 py-4 border-b border-slate-100">
        <div className="h-4 bg-slate-100 rounded flex-1" />
        <div className="h-4 bg-slate-100 rounded w-20" />
        <div className="h-4 bg-slate-100 rounded w-16" />
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

const ErrorState = ({ message, onRetry }) => (
  <div className="bg-white rounded-xl border border-red-100 shadow-sm p-8 text-center">
    <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
    <p className="text-sm text-slate-600 mb-4">{message || 'Une erreur est survenue'}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 transition-colors"
      >
        <RotateCcw className="w-4 h-4" />
        Reessayer
      </button>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const SuperAdmin = () => {
  const [activeTab, setActiveTab] = useState('structures');
  const [stats, setStats] = useState(null);
  const [crossAppData, setCrossAppData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [structures, setStructures] = useState([]);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [systemSettings, setSystemSettings] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [catalogApps, setCatalogApps] = useState([]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tabLoading, setTabLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/api/superadmin/stats');
      setStats(res.data);
      return res.data;
    } catch (_err) {
      // Stats might fail, continue
      return null;
    }
  }, []);

  const fetchCrossApp = useCallback(async () => {
    try {
      const res = await api.get('/api/superadmin/stats/cross-app');
      setCrossAppData(res.data);
    } catch (_err) {
      // Cross-app might fail
    }
  }, []);

  // Rechargement silencieux du catalogue : sans `tabLoading`, donc sans le
  // squelette qui demonterait l'onglet Modules — et avec lui la cle produit
  // qui vient d'etre generee et n'est affichee qu'une fois.
  const refreshCatalogApps = useCallback(async () => {
    try {
      const res = await api.get('/api/apps');
      setCatalogApps(Array.isArray(res.data) ? res.data : []);
    } catch (_err) {
      // La liste precedente reste affichee
    }
  }, []);

  const fetchTabData = useCallback(async (tab) => {
    setTabLoading(true);
    try {
      let res;
      switch (tab) {
        case 'structures':
          res = await api.get('/api/superadmin/structures');
          setStructures(Array.isArray(res.data) ? res.data : res.data.structures || res.data.items || []);
          break;
        case 'subscriptions':
          res = await api.get('/api/superadmin/subscriptions');
          setAllSubscriptions(Array.isArray(res.data) ? res.data : res.data.subscriptions || res.data.items || []);
          break;
        case 'packages':
          // Packages come from stats, no separate fetch needed
          break;
        case 'modules':
          res = await api.get('/api/apps');
          setCatalogApps(Array.isArray(res.data) ? res.data : []);
          break;
        case 'invoices':
          res = await api.get('/api/superadmin/invoices');
          setAllInvoices(Array.isArray(res.data) ? res.data : res.data.invoices || res.data.items || []);
          break;
        case 'settings':
          res = await api.get('/api/superadmin/system-settings');
          setSystemSettings(
            Array.isArray(res.data)
              ? res.data
              : Object.entries(res.data).map(([key, value]) => ({
                  key,
                  value: typeof value === 'string' ? value : JSON.stringify(value),
                }))
          );
          break;
        case 'audit':
          res = await api.get('/api/superadmin/audit-logs');
          setAuditLogs(Array.isArray(res.data) ? res.data : res.data.logs || res.data.items || []);
          break;
        default:
          break;
      }
    } catch (_err) {
      // Allow individual tab failures
    } finally {
      setTabLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([fetchStats(), fetchCrossApp()]);
        await fetchTabData('structures');
      } catch (err) {
        setError('Impossible de charger les donnees');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [fetchStats, fetchCrossApp, fetchTabData]);

  useEffect(() => {
    if (!loading) {
      setSearch('');
      setStatusFilter('');
      fetchTabData(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchStats(), fetchCrossApp(), fetchTabData(activeTab)]);
      toast.success('Donnees actualisees');
    } catch (_err) {
      toast.error('Erreur lors du rafraichissement');
    } finally {
      setRefreshing(false);
    }
  };

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const handleStructureAction = async (structureId, action) => {
    setActionLoading(structureId);
    try {
      await api.put(`/api/superadmin/structures/${structureId}/${action}`);
      toast.success(`Structure ${action === 'activate' ? 'activee' : 'suspendue'}`);
      fetchTabData('structures');
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantFree = async (subscriptionId) => {
    setActionLoading(subscriptionId);
    try {
      await api.post(`/api/superadmin/subscriptions/${subscriptionId}/grant-free`);
      toast.success('Abonnement gratuit accorde');
      fetchTabData('subscriptions');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveSetting = async (key, value) => {
    try {
      await api.put('/api/superadmin/system-settings', { key, value });
      toast.success('Parametre mis a jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const exportInvoices = async () => {
    try {
      const res = await api.get('/api/superadmin/invoices/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'factures-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (_err) {
      toast.error("Impossible d'exporter les factures");
    }
  };

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const revenueData = stats?.revenue?.trend_6_months || stats?.revenue_trend || [];
  const planDistribution = stats?.subscriptions?.by_plan || stats?.plan_distribution || {};
  const packages = stats?.packages || [];

  // -------------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <div className="h-7 w-64 bg-slate-100 rounded animate-pulse mb-2" />
            <div className="h-4 w-48 bg-slate-50 rounded animate-pulse" />
          </div>
        </div>
        {/* Stat cards skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
        {/* Charts skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
        <TableSkeleton />
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Error state
  // -------------------------------------------------------------------------

  if (error && !stats) {
    return (
      <ErrorState
        message={error}
        onRetry={() => {
          setError(null);
          setLoading(true);
          Promise.all([fetchStats(), fetchCrossApp()])
            .then(() => fetchTabData('structures'))
            .finally(() => setLoading(false));
        }}
      />
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-nexus-600" />
            Administration Plateforme
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Gestion globale de la plateforme Nexus-360
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50"
        >
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          Actualiser
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {STAT_CARD_CONFIG.map((card) => {
          const Icon = card.icon;
          const value = stats ? card.extract(stats) : null;
          return (
            <div
              key={card.key}
              className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${card.iconBg} mb-3`}>
                <Icon className={`w-5 h-5 ${card.iconColor}`} />
              </div>
              <p className="text-3xl font-bold text-slate-900 leading-tight">
                {formatStatValue(card.format, value)}
              </p>
              <p className="text-sm text-slate-500 mt-1">{card.label}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-slate-400" />
            <h2 className="font-semibold text-slate-900">Tendance des revenus</h2>
          </div>
          <RevenueChart data={revenueData} />
        </div>

        {/* Plan Distribution */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 text-slate-400" />
            <h2 className="font-semibold text-slate-900">Repartition par plan</h2>
          </div>
          <PlanDistribution data={planDistribution} />
        </div>
      </div>

      {/* Cross-App KPIs */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <AppWindow className="w-5 h-5 text-slate-400" />
          <h2 className="font-semibold text-slate-900">KPIs Inter-Applications</h2>
        </div>
        <CrossAppKPIs data={crossAppData} />
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-1 -mb-px overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  activeTab === tab.key
                    ? 'border-nexus-600 text-nexus-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                )}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {tabLoading ? (
          <TableSkeleton />
        ) : (
          <>
            {/* ============================================================= */}
            {/* Structures Tab                                                 */}
            {/* ============================================================= */}
            {activeTab === 'structures' && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 min-w-[200px] max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Rechercher une structure..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500"
                    />
                  </div>
                  <div className="relative">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="appearance-none pl-3 pr-8 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 bg-white"
                    >
                      <option value="">Tous les statuts</option>
                      <option value="active">Actif</option>
                      <option value="trial">Essai</option>
                      <option value="suspended">Suspendu</option>
                      <option value="inactive">Inactif</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left font-semibold text-slate-700 px-4 py-3">Structure</th>
                          <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden md:table-cell">SIRET</th>
                          <th className="text-left font-semibold text-slate-700 px-4 py-3">Statut</th>
                          <th className="text-center font-semibold text-slate-700 px-4 py-3 hidden lg:table-cell">Utilisateurs</th>
                          <th className="text-right font-semibold text-slate-700 px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {structures
                          .filter((s) => {
                            const m =
                              !search ||
                              (s.nom || '').toLowerCase().includes(search.toLowerCase()) ||
                              (s.siret || '').includes(search);
                            const st = !statusFilter || s.status === statusFilter;
                            return m && st;
                          })
                          .map((s) => (
                            <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-3">
                                <p className="font-medium text-slate-900">{s.nom}</p>
                                <p className="text-xs text-slate-500">{s.ville || ''}</p>
                              </td>
                              <td className="px-4 py-3 text-slate-600 font-mono text-xs hidden md:table-cell">
                                {s.siret || '--'}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                                    STATUS_BADGE[s.status] || STATUS_BADGE.inactive
                                  }`}
                                >
                                  {s.status || 'active'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center text-slate-600 hidden lg:table-cell">
                                {s.users_count ?? s.user_count ?? s.nb_users ?? '--'}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {s.status !== 'suspended' ? (
                                    <button
                                      onClick={() => handleStructureAction(s.id, 'suspend')}
                                      disabled={actionLoading === s.id}
                                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                                    >
                                      {actionLoading === s.id ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Pause className="w-3 h-3" />
                                      )}
                                      Suspendre
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleStructureAction(s.id, 'activate')}
                                      disabled={actionLoading === s.id}
                                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50"
                                    >
                                      {actionLoading === s.id ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Play className="w-3 h-3" />
                                      )}
                                      Activer
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        {structures.filter((s) => {
                          const m =
                            !search ||
                            (s.nom || '').toLowerCase().includes(search.toLowerCase()) ||
                            (s.siret || '').includes(search);
                          const st = !statusFilter || s.status === statusFilter;
                          return m && st;
                        }).length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                              Aucune structure trouvee
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================= */}
            {/* Subscriptions Tab                                              */}
            {/* ============================================================= */}
            {activeTab === 'subscriptions' && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left font-semibold text-slate-700 px-4 py-3">Structure</th>
                        <th className="text-left font-semibold text-slate-700 px-4 py-3">Application</th>
                        <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden md:table-cell">Plan</th>
                        <th className="text-left font-semibold text-slate-700 px-4 py-3">Statut</th>
                        <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden lg:table-cell">Debut</th>
                        <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden lg:table-cell">Fin</th>
                        <th className="text-right font-semibold text-slate-700 px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {allSubscriptions.map((sub) => (
                        <tr key={sub.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-slate-900">
                            {sub.structure?.nom || sub.structure_name || '--'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-nexus-50 text-nexus-700 capitalize">
                              {sub.app_code || sub.app?.name || '--'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-600 hidden md:table-cell capitalize">
                            {sub.package?.name || sub.plan_name || sub.package_code || sub.plan || '--'}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                                STATUS_BADGE[sub.status] || STATUS_BADGE.active
                              }`}
                            >
                              {sub.status || 'active'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs hidden lg:table-cell">
                            {sub.start_date || sub.created_at
                              ? new Date(sub.start_date || sub.created_at).toLocaleDateString('fr-FR')
                              : '--'}
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs hidden lg:table-cell">
                            {sub.end_date || sub.expires_at
                              ? new Date(sub.end_date || sub.expires_at).toLocaleDateString('fr-FR')
                              : '--'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleGrantFree(sub.id)}
                              disabled={actionLoading === sub.id}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-nexus-600 hover:bg-nexus-50 transition-colors disabled:opacity-50 ml-auto"
                            >
                              {actionLoading === sub.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Gift className="w-3 h-3" />
                              )}
                              Gratuit
                            </button>
                          </td>
                        </tr>
                      ))}
                      {allSubscriptions.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                            Aucun abonnement
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ============================================================= */}
            {/* Packages Tab                                                   */}
            {/* ============================================================= */}
            {activeTab === 'packages' && (
              <PackagesManager
                packages={packages}
                onRefresh={() => {
                  fetchStats();
                }}
              />
            )}

            {/* ============================================================= */}
            {/* Modules Tab — reglages SSO et cles produit                     */}
            {/* ============================================================= */}
            {activeTab === 'modules' && (
              <ModulesSSO apps={catalogApps} onRefresh={refreshCatalogApps} />
            )}

            {/* ============================================================= */}
            {/* Invoices Tab                                                   */}
            {/* ============================================================= */}
            {activeTab === 'invoices' && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button
                    onClick={exportInvoices}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Exporter CSV
                  </button>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left font-semibold text-slate-700 px-4 py-3">Facture</th>
                          <th className="text-left font-semibold text-slate-700 px-4 py-3">Structure</th>
                          <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden md:table-cell">Date</th>
                          <th className="text-right font-semibold text-slate-700 px-4 py-3">Montant</th>
                          <th className="text-left font-semibold text-slate-700 px-4 py-3">Statut</th>
                          <th className="text-right font-semibold text-slate-700 px-4 py-3">Telecharger</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {allInvoices.map((inv) => (
                          <tr key={inv.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3 font-medium text-slate-900">
                              #{inv.number || inv.invoice_number || inv.id}
                            </td>
                            <td className="px-4 py-3 text-slate-600">
                              {inv.structure?.nom || inv.structure_name || '--'}
                            </td>
                            <td className="px-4 py-3 text-slate-600 hidden md:table-cell">
                              {inv.invoice_date || inv.date || inv.created_at
                                ? new Date(inv.invoice_date || inv.date || inv.created_at).toLocaleDateString('fr-FR')
                                : '--'}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-900">
                              {inv.amount_ttc != null
                                ? inv.amount_ttc.toFixed(2)
                                : inv.amount != null
                                  ? inv.amount.toFixed(2)
                                  : '--'}{' '}
                              EUR
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                                  STATUS_BADGE[inv.status] || 'bg-green-100 text-green-700'
                                }`}
                              >
                                {inv.status || 'paid'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {(inv.pdf_url || inv.download_url) && (
                                <a
                                  href={inv.pdf_url || inv.download_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-nexus-600 hover:text-nexus-700 font-medium"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  PDF
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                        {allInvoices.length === 0 && (
                          <tr>
                            <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                              Aucune facture
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================= */}
            {/* Settings Tab                                                   */}
            {/* ============================================================= */}
            {activeTab === 'settings' && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Parametres systeme
                </h3>
                <div className="space-y-3">
                  {systemSettings.length > 0 ? (
                    systemSettings.map((setting, idx) => (
                      <div
                        key={setting.key || idx}
                        className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg"
                      >
                        <label className="text-sm font-medium text-slate-700 w-48 flex-shrink-0 font-mono">
                          {setting.key}
                        </label>
                        <input
                          type="text"
                          value={setting.value || ''}
                          onChange={(e) => {
                            setSystemSettings((prev) =>
                              prev.map((s, i) =>
                                i === idx ? { ...s, value: e.target.value } : s
                              )
                            );
                          }}
                          className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500"
                        />
                        <button
                          onClick={() => handleSaveSetting(setting.key, setting.value)}
                          className="p-2 rounded-lg text-nexus-600 hover:bg-nexus-50 transition-colors"
                          title="Enregistrer"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">
                      Aucun parametre configure
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ============================================================= */}
            {/* Audit Logs Tab                                                 */}
            {/* ============================================================= */}
            {activeTab === 'audit' && (
              <div className="space-y-4">
                <div className="relative max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Filtrer les logs..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500"
                  />
                </div>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left font-semibold text-slate-700 px-4 py-3">Date</th>
                          <th className="text-left font-semibold text-slate-700 px-4 py-3">Utilisateur</th>
                          <th className="text-left font-semibold text-slate-700 px-4 py-3">Action</th>
                          <th className="text-left font-semibold text-slate-700 px-4 py-3 hidden md:table-cell">
                            Details
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {auditLogs
                          .filter((log) => {
                            if (!search) return true;
                            const text =
                              `${log.user_email || ''} ${log.action || ''} ${log.details || ''}`.toLowerCase();
                            return text.includes(search.toLowerCase());
                          })
                          .map((log, idx) => (
                            <tr key={log.id || idx} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                                <div className="flex items-center gap-1.5">
                                  <Clock className="w-3.5 h-3.5" />
                                  {log.timestamp || log.created_at
                                    ? new Date(log.timestamp || log.created_at).toLocaleString(
                                        'fr-FR',
                                        { dateStyle: 'short', timeStyle: 'short' }
                                      )
                                    : '--'}
                                </div>
                              </td>
                              <td className="px-4 py-3 font-medium text-slate-900">
                                {log.user_email || log.user?.email || '--'}
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded font-mono">
                                  {log.action || log.event_type || '--'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-500 text-xs hidden md:table-cell max-w-xs truncate">
                                {log.details || log.description || '--'}
                              </td>
                            </tr>
                          ))}
                        {auditLogs.filter((log) => {
                          if (!search) return true;
                          const text =
                            `${log.user_email || ''} ${log.action || ''} ${log.details || ''}`.toLowerCase();
                          return text.includes(search.toLowerCase());
                        }).length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-4 py-12 text-center text-slate-500">
                              Aucun log d'audit
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SuperAdmin;
